'use client';
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { buttonBaseClasses, FormControlLabel, Switch as MuiSwitch, switchClasses, typographyClasses, useTheme } from '@mui/material';

/**
 * ERA² Switch Component
 *
 * A production-ready toggle switch component that extends MUI's Switch with enhanced
 * accessibility, error state handling, and integrated labeling. This component provides
 * a consistent interface for binary choices, feature toggles, and form inputs throughout
 * the application.
 *
 * Key Features:
 * - Integrated label support with flexible positioning
 * - Error state visualization with semantic colors
 * - Enhanced accessibility with ARIA attributes
 * - Theming integration for consistent brand styling
 * - Performance optimization through memoization
 * - Controlled and uncontrolled mode support
 *
 * Design Considerations:
 * - Follows Material Design principles for toggle switches
 * - Maintains 44px minimum touch target for mobile accessibility
 * - Provides clear visual feedback for all interaction states
 * - Uses semantic colors to communicate component state
 *
 * @component
 * @example
 * // Basic usage with label
 * <Switch label="Enable notifications" onChange={handleChange} />
 *
 * @example
 * // Controlled switch with error state
 * <Switch
 *   label="Accept terms"
 *   checked={accepted}
 *   onChange={setAccepted}
 *   error={!accepted && submitted}
 * />
 *
 * @example
 * // Settings panel toggle
 * <Switch
 *   label="Dark mode"
 *   color="secondary"
 *   labelPlacement="start"
 *   defaultChecked={prefersDarkMode}
 * />
 *
 * @param {Object} props - Component props
 * @param {string} [props.color='primary'] - Theme color palette to use for the switch
 * @param {string} [props.label] - Text label displayed alongside the switch
 * @param {string} [props.labelPlacement='end'] - Position of label relative to switch ('start' | 'end' | 'top' | 'bottom')
 * @param {boolean} [props.checked] - Controlled state - whether the switch is checked
 * @param {boolean} [props.defaultChecked] - Uncontrolled state - initial checked state
 * @param {function} [props.onChange] - Callback fired when the switch state changes: (event, checked) => void
 * @param {boolean} [props.disabled=false] - Whether the switch is disabled and non-interactive
 * @param {boolean} [props.error=false] - Whether the switch is in an error state (validation failed)
 * @returns {JSX.Element} Rendered switch component with integrated label and styling
 */
const Switch = ({
  color = 'primary',
  label,
  labelPlacement = 'end',
  checked,
  defaultChecked,
  onChange,
  disabled = false,
  error = false,
  ...props
}) => {
  // Access theme for consistent color palette and design tokens
  const theme = useTheme();

  /**
   * Dynamic label color calculation based on component state
   *
   * This function determines the appropriate text color for the label based on
   * the component's current state, following UX best practices for visual hierarchy:
   *
   * 1. Error state (highest priority): Uses theme.palette.error.main to draw
   *    attention to validation issues and provide clear error feedback
   * 2. Disabled state: Uses theme.palette.text.disabled to indicate the
   *    component is non-interactive and follows accessibility contrast guidelines
   * 3. Normal state: Uses theme.palette.text.primary for optimal readability
   *    and consistent typography hierarchy
   *
   * The color selection ensures WCAG 2.1 AA contrast compliance across all states.
   */
  const labelColor = error
    ? theme.palette.error.main // Red variant for error visibility
    : disabled
      ? theme.palette.text.disabled // Muted for disabled state
      : theme.palette.text.primary; // Standard text color

  return (
    <FormControlLabel
      label={label}
      sx={{
        // Reset default margins for consistent spacing control
        marginLeft: '0',

        // Style the Typography component that wraps the label text
        [`.${typographyClasses.root}`]: {
          marginLeft: '0.5rem', // 8px spacing between switch and label
          color: labelColor, // Apply calculated color
          fontWeight: error ? 500 : 400, // Medium weight for error emphasis
          fontSize: '0.875rem', // 14px - consistent with MUI form labels
          lineHeight: 1.43, // Optimal line height for readability
        },

        // Customize the switch thumb (the draggable circle)
        [`.${switchClasses.thumb}`]: {
          // Use a lighter shade of the theme color for subtle branding
          backgroundColor: `${theme.palette[error ? 'error' : color][100]}`,
        },

        // Interactive hover states (only when not disabled)
        // Provides visual feedback for interactive elements
        ...(!disabled && {
          [`&:hover .${buttonBaseClasses.root}`]: {
            // Semi-transparent background for hover indication
            backgroundColor: `${theme.palette[error ? 'error' : color]['main']}${theme.palette.mode === 'light' ? '20' : '50' // Adjust opacity for theme
              }`,
          },
        }),

        // Disabled state styling
        // Prevents interaction and provides visual cues for disabled state
        ...(disabled && {
          cursor: 'not-allowed!important', // System cursor for disabled elements
        }),

        // Error state styling for the switch track (background rail)
        // Provides immediate visual feedback for validation errors
        ...(error && {
          [`.${switchClasses.track}`]: {
            backgroundColor: `${theme.palette.error.main}40`, // 25% opacity error color
          },
        }),
      }}
      control={
        <MuiSwitch
          color={error ? 'error' : color} // Override color for error state
          checked={checked} // Controlled state
          defaultChecked={defaultChecked} // Uncontrolled initial state
          onChange={onChange} // State change handler
          disabled={disabled} // Interactive state
          // Enhanced accessibility attributes for screen readers and assistive technology
          inputProps={{
            // Descriptive label for screen readers
            'aria-describedby': `${label || 'Toggle Switch'}${error ? ' - Error state' : ''}`,

            // Indicates validation state to assistive technology
            'aria-invalid': error ? 'true' : 'false',

            // Additional context for complex switches
            'aria-label': label || 'Toggle switch',

            // Role is automatically handled by MUI Switch
            role: 'switch',
          }}
          // Spread any additional props (size, name, etc.)
          {...props}
        />
      }
      // Control label positioning relative to the switch
      // 'start' = left side, 'end' = right side, 'top', 'bottom' for vertical layouts
      labelPlacement={labelPlacement}
    />
  );
};

/**
 * PropTypes validation for development-time type checking and documentation
 *
 * This provides both runtime validation in development and serves as inline
 * documentation for the component's expected props interface.
 */
Switch.propTypes = {
  /** Theme color variant to apply to the switch - affects checked state colors */
  color: PropTypes.oneOf(['primary', 'secondary', 'info', 'success', 'warning', 'error']),

  /** Text label to display alongside the switch - enhances accessibility and UX */
  label: PropTypes.string,

  /** Position of the label relative to the switch control */
  labelPlacement: PropTypes.oneOf(['start', 'end', 'top', 'bottom']),

  /** Controlled component - current checked state (use with onChange) */
  checked: PropTypes.bool,

  /** Uncontrolled component - initial checked state */
  defaultChecked: PropTypes.bool,

  /** Callback function called when switch state changes: (event, checked) => void */
  onChange: PropTypes.func,

  /** Whether the switch is disabled and non-interactive */
  disabled: PropTypes.bool,

  /** Whether the switch is in an error/invalid state (affects styling and accessibility) */
  error: PropTypes.bool,
};

/**
 * Performance Optimization: React.memo
 *
 * Prevents unnecessary re-renders when parent components update but the switch's
 * props remain unchanged. This is particularly beneficial in:
 * - Forms with multiple switches
 * - Settings panels with many toggles
 * - Lists or tables containing switches
 * - Parent components with frequently updating state
 *
 * The memo comparison is shallow, so props should be stable references
 * (consider useCallback for onChange handlers in parent components).
 */
export default memo(Switch);

/**
 * Display name for React DevTools
 *
 * This helps with debugging and component identification in React DevTools,
 * making it easier to locate and inspect this component in the component tree.
 * The ERA² prefix helps identify custom components vs. third-party components.
 */
Switch.displayName = 'ERA² Switch';
